/** Deep-linkable, mutually exclusive selection for the booking detail page (`/booking/$id`). */

export type BookingUrlPanel = 'event' | 'questionnaire'

export type BookingSearchParams = {
  token?: string
  session_id?: string
  panel?: BookingUrlPanel
  invoice?: string
}

/**
 * `validateSearch` for `/booking/$id`. Unknown/invalid `panel` values fall back to undefined
 * (invoice). `panel` and `invoice` are mutually exclusive — if a valid `panel` is present, any
 * `invoice` param is dropped so the URL never carries both selections at once.
 */
export function validateBookingSearch(search: Record<string, unknown>): BookingSearchParams {
  const panel = search.panel === 'event' || search.panel === 'questionnaire' ? search.panel : undefined
  return {
    token: typeof search.token === 'string' ? search.token : undefined,
    session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
    panel,
    invoice: !panel && typeof search.invoice === 'string' ? search.invoice : undefined,
  }
}

export type BookingSelection =
  | { panel: 'invoice'; invoiceId: string }
  | { panel: 'event' }
  | { panel: 'questionnaire' }
  | { panel: 'none' }

/**
 * Resolve the single active left-rail/right-panel selection from the URL and loaded booking
 * data. `panel` always wins over `invoice` (validateBookingSearch already enforces this at the
 * URL level, but the resolver re-checks so callers can't accidentally pass both). A stale/invalid
 * `panel` (event with no events, questionnaire with no items) falls back to the default
 * invoice; a stale/invalid `invoice` id falls back to the first invoice. Answered questionnaires
 * stay selectable — `showQuestionnaire` is about presence, not outstanding questions.
 *
 * While the questionnaire gates payment, it is the default even over an `invoice` deep link
 * (email pay links carry one): the customer must answer it before they can pay. Only an invoice
 * the customer picks on the page, or the event panel, overrides that. Once the questionnaire is
 * answered the gate drops and the deep-linked invoice shows again.
 */
export function resolveBookingSelection(params: {
  panelFromUrl: BookingUrlPanel | undefined
  invoiceFromUrl: string | undefined
  invoiceIds: string[] | undefined | null
  showEvent: boolean | undefined
  showQuestionnaire: boolean | undefined
  questionnaireGated?: boolean
  invoicePickedOnPage?: boolean
}): BookingSelection {
  const { panelFromUrl, invoiceFromUrl, invoiceIds, showEvent, showQuestionnaire } = params

  if (panelFromUrl === 'questionnaire' && showQuestionnaire) {
    return { panel: 'questionnaire' }
  }
  if (
    params.questionnaireGated
    && showQuestionnaire
    && panelFromUrl !== 'event'
    && !params.invoicePickedOnPage
  ) {
    return { panel: 'questionnaire' }
  }
  if (panelFromUrl === 'event' && showEvent) {
    return { panel: 'event' }
  }

  const invoiceId = invoiceFromUrl && invoiceIds?.includes(invoiceFromUrl)
    ? invoiceFromUrl
    : invoiceIds?.[0] ?? null

  return invoiceId ? { panel: 'invoice', invoiceId } : { panel: 'none' }
}

/**
 * The invoice the questionnaire panel hands the customer back to, on save or on "Back to
 * invoice": the one they were viewing when they opened it, else the pay link's, else the first.
 * Ids no longer on the booking are skipped. Null when the booking has no invoices.
 */
export function questionnaireReturnInvoiceId(params: {
  invoiceViewedBefore: string | null | undefined
  invoiceFromUrl: string | undefined
  invoiceIds: string[] | undefined | null
}): string | null {
  const ids = params.invoiceIds ?? []
  for (const candidate of [params.invoiceViewedBefore, params.invoiceFromUrl]) {
    if (candidate && ids.includes(candidate)) return candidate
  }
  return ids[0] ?? null
}

/** Search params to apply when redirecting from payment success to the booking page. */
export function paymentSuccessBookingSearch(params: {
  tipOnly: boolean
  hasUnansweredQuestionnaire: boolean
}): { panel: 'questionnaire' } | Record<string, never> {
  if (params.tipOnly || !params.hasUnansweredQuestionnaire) {
    return {}
  }
  return { panel: 'questionnaire' }
}
